import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from '@/i18n/index.jsx';
import { getTranslatedCategories } from '@/utils/conversionDataI18n.js';
import { useConverterCache } from '@/hooks/useConverter.js';
import TimezoneConverter from './TimezoneConverter.jsx';

function formatResult(n) {
  if (n === '' || n === null || n === undefined || isNaN(n)) return '';
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 0.000001 || abs > 1000000)) return n.toExponential(6);
  // Up to 6 significant digits, strip trailing zeros without mangling whole numbers.
  return parseFloat(n.toPrecision(6)).toString();
}

function ConverterApp({ initialCategoryId = 'common', initialConverterId = 'length', initialFromUnit = null, initialToUnit = null }) {
  const { lang, dict } = useI18n();
  const isEN = lang === 'en';
  const { currencyData: exchangeRates, isCurrencyLoading } = useConverterCache();
  const categories = useMemo(() => getTranslatedCategories(dict, lang), [dict, lang]);

  const [selectedCategory, setSelectedCategory] = useState(initialCategoryId);
  const [selectedConverter, setSelectedConverter] = useState(initialConverterId);

  // Calcula as unidades iniciais de forma síncrona (antes da primeira pintura),
  // para os seletores já nascerem preenchidos e evitar o "pulo" de layout (CLS).
  const initialCategoryObj = categories.find(c => c.id === initialCategoryId) || categories[0];
  const initialConverterObj = initialCategoryObj.converters.find(c => c.id === initialConverterId) || initialCategoryObj.converters[0];
  const initialUnitsList = initialConverterObj.units || [];

  const [fromUnit, setFromUnit] = useState(() => {
    if (initialFromUnit && initialUnitsList.find(u => u.id === initialFromUnit)) return initialFromUnit;
    return initialUnitsList[0]?.id || '';
  });
  const [toUnit, setToUnit] = useState(() => {
    if (initialToUnit && initialUnitsList.find(u => u.id === initialToUnit)) return initialToUnit;
    return initialUnitsList.length > 1 ? initialUnitsList[1].id : (initialUnitsList[0]?.id || '');
  });
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  useEffect(() => {
    setSelectedCategory(initialCategoryId);
    setSelectedConverter(initialConverterId);
  }, [initialCategoryId, initialConverterId]);

  const currentCategoryObj = categories.find(c => c.id === selectedCategory) || categories[0];
  const currentConverterObj = currentCategoryObj.converters.find(c => c.id === selectedConverter) || currentCategoryObj.converters[0];
  const units = currentConverterObj.units || [];

  const isSizeSelectConverter = ['tamanho-camiseta', 'tamanho-calca', 'tamanho-tenis'].includes(currentConverterObj.id);
  const isTextInputConverter = !!currentConverterObj.isTextInput;
  const isTimezoneConverter = !!currentConverterObj.isTimezone;

  const fromSizeOptions = useMemo(() => {
    if (!isSizeSelectConverter) return [];
    const opts = currentConverterObj.sizeOptions?.[fromUnit];
    if (opts && opts.length) return opts;
    // fallback tênis BR 13–55
    if (currentConverterObj.id === 'tamanho-tenis') {
      return Array.from({ length: 43 }, (_, i) => String(13 + i));
    }
    return ['PP', 'P', 'M', 'G', 'GG', 'XGG'];
  }, [isSizeSelectConverter, currentConverterObj, fromUnit]);

  const toSizeOptions = useMemo(() => {
    if (!isSizeSelectConverter) return [];
    const opts = currentConverterObj.sizeOptions?.[toUnit];
    if (opts && opts.length) return opts;
    if (currentConverterObj.id === 'tamanho-tenis') {
      return Array.from({ length: 43 }, (_, i) => String(13 + i));
    }
    return ['PP', 'P', 'M', 'G', 'GG', 'XGG'];
  }, [isSizeSelectConverter, currentConverterObj, toUnit]);

  useEffect(() => {
    if (units.length > 0) {
      if (initialFromUnit && units.find(u => u.id === initialFromUnit)) {
        setFromUnit(initialFromUnit);
      } else {
        setFromUnit(units[0].id);
      }
      
      if (initialToUnit && units.find(u => u.id === initialToUnit)) {
        setToUnit(initialToUnit);
      } else {
        setToUnit(units.length > 1 ? units[1].id : units[0].id);
      }

      // Valor inicial da lista suspensa (camiseta / calça / tênis)
      if (['tamanho-camiseta', 'tamanho-calca', 'tamanho-tenis'].includes(currentConverterObj.id)) {
        const opts = currentConverterObj.sizeOptions?.[units[0]?.id] || ['PP', 'P', 'M', 'G', 'GG', 'XGG'];
        const defaultVal = currentConverterObj.id === 'tamanho-tenis' ? '40' : (opts[2] || opts[0]); // M ou 40
        setFromValue(String(defaultVal));
      }
    }
  }, [selectedConverter, units, initialFromUnit, initialToUnit, currentConverterObj]);

  const convertValue = useCallback((value, fromUnitId, toUnitId) => {
    if (value === '' || value === null || value === undefined) return '';

    // Conversores com fórmula customizada (camiseta, calça, tênis, etc.)
    // Aceita letras (PP, M, G...) e números
    if (typeof currentConverterObj.customConvert === 'function') {
      const result = currentConverterObj.customConvert(value, fromUnitId, toUnitId);
      if (result === undefined || result === null || result === '') return '';
      if (typeof result === 'string') return result;
      if (typeof result === 'number' && !isNaN(result)) {
        return String(result);
      }
      return String(result);
    }

    if (isNaN(value)) return '';
    const numericValue = parseFloat(value);

    if (currentConverterObj.isCurrency) {
      if (!exchangeRates || !exchangeRates[fromUnitId] || !exchangeRates[toUnitId]) return '';
      const result = (numericValue * exchangeRates[toUnitId]) / exchangeRates[fromUnitId];
      return formatResult(result);
    }

    if (currentConverterObj.isTemperature) {
      let celsius;
      switch (fromUnitId) {
        case 'f': celsius = (numericValue - 32) * 5/9; break;
        case 'k': celsius = numericValue - 273.15; break;
        default: celsius = numericValue;
      }
      let result;
      switch (toUnitId) {
        case 'f': result = (celsius * 9/5) + 32; break;
        case 'k': result = celsius + 273.15; break;
        default: result = celsius;
      }
      return formatResult(result);
    }

    const fromFactor = units.find(u => u.id === fromUnitId)?.factor || 1;
    const toFactor = units.find(u => u.id === toUnitId)?.factor || 1;

    // Convention: factor = base units per 1 of this unit (e.g. km = 1000 m).
    // value_base = value * fromFactor; result = value_base / toFactor.
    return formatResult(numericValue * (fromFactor / toFactor));
  }, [currentConverterObj, units, exchangeRates]);

  useEffect(() => {
    if (fromUnit && toUnit) {
      setToValue(convertValue(fromValue, fromUnit, toUnit));
    }
  }, [fromValue, fromUnit, toUnit, convertValue]);

  const handleFromValueChange = (e) => {
    const val = e.target.value.replace(/,/g, '.');
    setFromValue(val);
  };

  const handleToValueChange = (e) => {
    const val = e.target.value.replace(/,/g, '.');
    setToValue(val);
    if (val === '') {
      setFromValue('');
    } else {
      setFromValue(convertValue(val, toUnit, fromUnit));
    }
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <div className="bg-card border border-border shadow-lg rounded-3xl p-6 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="space-y-2">
          <Label htmlFor="category-select">{isEN ? 'Category' : 'Categoria'}</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger id="category-select" className="bg-background text-foreground">
              <SelectValue placeholder={isEN ? 'Select category' : 'Selecione a categoria'} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="converter-select">{isEN ? 'Converter' : 'Conversor'}</Label>
          <Select value={selectedConverter} onValueChange={setSelectedConverter}>
            <SelectTrigger id="converter-select" className="bg-background text-foreground">
              <SelectValue placeholder={isEN ? 'Select converter' : 'Selecione o conversor'} />
            </SelectTrigger>
            <SelectContent>
              {currentCategoryObj.converters.map(conv => (
                <SelectItem key={conv.id} value={conv.id}>{conv.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isTimezoneConverter ? (
        <TimezoneConverter initialFromUnit={initialFromUnit} initialToUnit={initialToUnit} />
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="w-full flex-1 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="from-value">{isEN ? 'Value' : 'Valor'}</Label>
            {isSizeSelectConverter ? (
              <Select value={String(fromValue)} onValueChange={(v) => setFromValue(v)}>
                <SelectTrigger id="from-value" className="text-2xl h-14 bg-background text-foreground font-medium">
                  <SelectValue placeholder={isEN ? 'Select size' : 'Selecione o tamanho'} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {fromSizeOptions.map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input 
                id="from-value" 
                type={isTextInputConverter ? 'text' : 'number'} 
                value={fromValue} 
                onChange={handleFromValueChange}
                className="text-2xl h-14 bg-background text-foreground font-medium"
                placeholder={isTextInputConverter ? (isEN ? 'e.g. 1010, FF, 1 1/2' : 'ex.: 1010, FF, 1 1/2') : '0.00'}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="from-unit">{isEN ? 'From' : 'De'}</Label>
            <Select value={fromUnit} onValueChange={setFromUnit}>
              <SelectTrigger id="from-unit" className="bg-background text-foreground h-12">
                <SelectValue placeholder={isEN ? 'Source unit' : 'Unidade de origem'} />
              </SelectTrigger>
              <SelectContent>
                {units.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} {u.symbol ? `(${u.symbol})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <button 
          onClick={swapUnits}
          className="mt-6 p-4 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex-shrink-0 group"
          aria-label={isEN ? 'Swap units' : 'Inverter unidades'}
        >
          <ArrowRightLeft className="w-6 h-6 transition-transform group-hover:rotate-180" />
        </button>

        <div className="w-full flex-1 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="to-value">{isEN ? 'Result' : 'Resultado'}</Label>
            {isSizeSelectConverter ? (
              <Input 
                id="to-value" 
                type="text" 
                value={toValue} 
                readOnly
                className="text-2xl h-14 bg-muted text-foreground font-bold border-transparent"
                placeholder={isEN ? 'Result' : 'Resultado'}
              />
            ) : (
              <Input 
                id="to-value" 
                type={isTextInputConverter ? 'text' : (currentConverterObj.isCurrency && isCurrencyLoading ? 'text' : 'number')} 
                value={currentConverterObj.isCurrency && isCurrencyLoading ? (isEN ? 'Loading rates...' : 'Carregando cotação...') : toValue} 
                onChange={handleToValueChange}
                readOnly={currentConverterObj.isCurrency && isCurrencyLoading}
                className="text-2xl h-14 bg-muted text-foreground font-bold border-transparent"
                placeholder="0.00"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="to-unit">{isEN ? 'To' : 'Para'}</Label>
            <Select value={toUnit} onValueChange={setToUnit}>
              <SelectTrigger id="to-unit" className="bg-background text-foreground h-12">
                <SelectValue placeholder={isEN ? 'Target unit' : 'Unidade de destino'} />
              </SelectTrigger>
              <SelectContent>
                {units.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} {u.symbol ? `(${u.symbol})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default ConverterApp;